import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import Modal from "components/modal/Modal";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { LeadProvider, RegisterData, RegistrationRoleType, useAddPhoneMfaDeviceMutation, useGetDecryptUserQuery, useGetUserDetailsQuery, usePostRegistrationMutation } from "./registrationService";
import * as Constant from "utilities/Constant";
import { emailAddress, phone, password_input, firstname, lastname, loanpurpose } from "utilities/formSchemas";
import "./registration.scss";
import { useDispatch, useSelector } from "react-redux";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getEmailAddress, getNhDealApplicationId, setCredentials, setLeadProvider, setUserId } from "features/authSlice";
import { ErrorType } from "utilities/error";
import { Consent } from "components/consent/Consent";
import useDesktopChecker from "hooks/useDesktopChecker";
import { setFSUserProperties, trackFSVisitedRegistrationPage } from "utilities/full-story";
import PasswordChecker from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import homeGraphic from "assets/images/HomeGraphic.png";
import { setResendCodeDeviceId, setAuthDeviceType } from "features/mfaSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import { RegistrationLegalConsent } from "components/registration-legal-consent/Registration-legal-consent";
import Footer from "components/footer/Footer";
import { useGetLoanOfficerByLoIdQuery, useGetLoanOfficerByNhDealApplicationIdOrInquiryIdQuery } from "pages/application/applicationService";
import LoanOfficerCardV2 from "components/v2/loan-officer-card/LoanOfficerCardV2";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";

const formSchema = z.object({
  email: emailAddress,
  firstname: firstname.optional(),
  lastname: lastname.optional(),
  phone: phone.optional(),
  loanpurpose: loanpurpose.optional(),
  password_input,
});

export default function Registration() {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(formSchema),
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useDesktopChecker();
  const encryptedUser = searchParams.get("t")!;
  const email = searchParams.get("email")!;
  const loanId = searchParams.get("loanId") ?? searchParams.get("lid")!;
  const loanOfficerId = searchParams.get("log");
  const dealId = searchParams.get("did")!;
  const prospectId = searchParams.get("pid")!;
  const leadProviderName = searchParams.get("lpn")!;
  const losId = searchParams.get("losId")!;
  const isEncryptedUser = !!encryptedUser;
  const isBonzoUrlParams = !!(email && loanId && leadProviderName);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const emailFromStore = useSelector(getEmailAddress);
  const nhDealApplicationIdFromStore = useSelector(getNhDealApplicationId);
  const {
    data: decryptedUserDetails,
    isLoading,
    isFetching,
    isSuccess: isDecryptUserSuccess,
  } = useGetDecryptUserQuery({ encryptedUser, email, loanId, leadProviderName, dealId, losId, prospectId }, { skip: !(isEncryptedUser || isBonzoUrlParams) });

  const nhDealAppId = decryptedUserDetails?.nhDealAppId ?? nhDealApplicationIdFromStore;
  const inquiryId = decryptedUserDetails?.id ?? "";
  const loLookUpId = (nhDealAppId && nhDealAppId !== "00000000-0000-0000-0000-000000000000") ? nhDealAppId : inquiryId;

  // Fetch by loId first (self-reg / "log" URL param path). If present, skip the
  // nhDealApp/inquiryId lookup entirely to avoid a redundant network call.
  const { data: loDataByLoId = null } = useGetLoanOfficerByLoIdQuery(
    loanOfficerId ?? "",
    { skip: !loanOfficerId }
  );

  // Skip if: decrypt hasn't succeeded yet, no valid lookup ID exists, or LO data
  const { data: loData = null } = useGetLoanOfficerByNhDealApplicationIdOrInquiryIdQuery(loLookUpId,
    { skip: !isDecryptUserSuccess || (!inquiryId && !nhDealAppId) || !!loanOfficerId }
  );

  const rawLoData = loDataByLoId ?? loData;
  const loanOfficer = rawLoData?.fullName && rawLoData.fullName !== "" && rawLoData.fullName !== "Call Queue" ? rawLoData : null;

  const {
    data: isPhoneRequired,
    isSuccess: isUserDetailsSuccess,
    isLoading: isUserDetailsLoading,
    isFetching: isPhoneNumberFetching,
  } = useGetUserDetailsQuery(emailFromStore || "", {
    skip: !emailFromStore,
  });

  useEffect(() => {
    if (decryptedUserDetails) {
      const { ex, fn, ln, em, ph, lp, lpn, rt, phmfapending } = decryptedUserDetails;
      if (!ex) {
        if (lpn) {
          dispatch(setLeadProvider(lpn));
        }
        setIsBtnDisabled((prevState) => !prevState);
        setValue("firstname", fn);
        setValue("lastname", ln);
        setValue("email", em);
        if (!phmfapending)
          setValue("phone", ph.replace(/-/g, ""));
        setValue("loanpurpose", lp ?? undefined);
        const isBonzoSpousalCoBorrower =
          leadProviderName?.toLowerCase() === LeadProvider.Bonzo.toLowerCase() &&
          rt?.toLowerCase() === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
        setIsBonzoSpouse(isBonzoSpousalCoBorrower);
      } else {
        navigate("/", { replace: true, state: decryptedUserDetails });
      }
    }
  }, [decryptedUserDetails]);


  useEffect(() => {
    if (!decryptedUserDetails) return;
    if (decryptedUserDetails.ex) return;

    const leadProvider = decryptedUserDetails.lpn ?? "Unknown";
    const loanType = decryptedUserDetails.lp ?? "Unknown";


    trackFSVisitedRegistrationPage({
      leadProvider: leadProvider,
      loanType: loanType,
    });
  }, [decryptedUserDetails]);

  useEffect(() => {
    if (emailFromStore) {
      setValue("email", emailFromStore);
    }
  }, [emailFromStore]);

  // Show Tracking Pixel
  const [showPixel, setShowPixel] = useState(false);
  useEffect(() => {
    if (
      featureFlag?.ttdCodeSnippetEnabled &&
      !isLoading &&
      !isFetching &&
      (decryptedUserDetails || leadProviderName)
    ) {
      setShowPixel(true);
    }
  }, [featureFlag?.ttdCodeSnippetEnabled, isLoading, isFetching, decryptedUserDetails, leadProviderName]);

  let showModal = false;
  if (!isLoading && !isFetching && !decryptedUserDetails && !emailFromStore) {
    showModal = true;
  }
  else if (emailFromStore) {
    setFSUserProperties(emailFromStore ?? "");
  }
  else {
    setFSUserProperties(decryptedUserDetails?.em ?? "", decryptedUserDetails?.ln.toLowerCase() ?? "");
  }

  const [error, setError] = useState(0);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [hasPwdError, setHasPwdError] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [isBonzoSpouse, setIsBonzoSpouse] = useState(false);
  const [sendRequest, { isSuccess, data, isLoading: isRegistrationLoading }] = usePostRegistrationMutation();
  const [addPhoneMfaDevice, { isLoading: isAddingMfaDeviceInProgress }] = useAddPhoneMfaDeviceMutation();
  const dispatch = useDispatch();

  const onSubmit = async (data: RegisterData) => {
    if (!hasErrors && !hasPwdError) {
      try {
        if ((isPhoneRequired || !!decryptedUserDetails?.phmfapending) && data.phoneNumber) {
          await addPhoneMfaDevice({ email: data.emailaddress, phoneNumber: data.phoneNumber }).unwrap();
        }
        sendRequest(data)
          .unwrap()
          .catch((error: ErrorType) => {
            if (error.status == 409) {
              navigate("/", { replace: true });
            } else {
              setError(500);
            }
          });
      } catch (error) {
        const err = error as ErrorType;
        if (err.status === 400 && err.data?.code === Constant.ErrorCodeInvalidMFA) {
          setPhoneErrorMessage("Enter Valid Cell Phone Number");
        } else {
          setError(500);
        }
      }
    }
  };

  if (isSuccess && data) {
    if (data.status === "OTP_REQUIRED") {
      dispatch(setUserId(data.id));
      dispatch(setResendCodeDeviceId(data.deviceAuthId));
      dispatch(setAuthDeviceType(data.deviceType ?? ""));
      navigate("/registrationAuth", { replace: true });
    }
    else if ("status" in data && data.status.toLowerCase() === "completed") {
      dispatch(setCredentials({
        ...data,
      }));
      navigate("/my-loans", { replace: true });
    }
    else if ("status" in data && data.status === "DEVICE_SELECTION_REQUIRED") {
      dispatch(setUserId(data.id));
      navigate("/mfa", { replace: true });
    } else {
      setError(500);
    }
  }
  const ispasswordFieldModified = (dirtyFields.password_input as boolean) || false;
  const hasErrors = !!Object.keys(errors).length;
  const phoneValue = watch("phone") as string | undefined;
  const phoneDigits = phoneValue?.replace(/\D/g, "") ?? "";
  const isPhoneValid = !(isPhoneRequired || !!decryptedUserDetails?.phmfapending) || phoneDigits.length === 10;

  if (isRegistrationLoading || isUserDetailsLoading || isAddingMfaDeviceInProgress) return <LoadingOverlay text="Loading..." />;
  const formatContent = (content: string[]) => {
    return (
      <ul className="registration-list">
        {content?.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {/* NAF_Lead Portal_Static_6.4.25 Tracking Pixel */}
      {showPixel && (
        <img
          height="1"
          width="1"
          style={{ borderStyle: "none" }}
          alt=""
          src={`${Constant.TTD_CODE_SNIPPET_URL}/?adv=ffdysrg&ct=0:vf1o9ix&fmt=3&td1=${encodeURIComponent(
            leadProviderName || decryptedUserDetails?.lpn || "Others"
          )}&td2=&td3=${encodeURIComponent(decryptedUserDetails?.id && decryptedUserDetails.id !== "00000000-0000-0000-0000-000000000000"
            ? decryptedUserDetails.id
            : "")}`}
        />
      )}

      {(isLoading || isFetching) && <LoadingOverlay text="Loading user details..." />}
      <div className="registration">
        {showModal && (
          <Modal
            defaultBody
            defaultBodyLabel="Something went wrong!"
            defaultBodyLabelIcon={regular("triangle-exclamation")}
            defaultBodySubContent={[
              "Please open the email we sent you and click on the link so that we can try again.",
              "We apologize for the inconvenience.",
            ]}
            footer={<></>}
          />
        )}
        <div className="registration__container">
          <div className="registration__form">
            <h1 className="registration__header">{isDesktop ? `Create an account to continue your application` : <>Create an account<br />to continue</>}</h1>
            {!isDesktop && loanOfficer && (
              <>
                <div className="registration__mobile-lo-invite">
                  <p>You&#39;ve been invited to start your application.</p>
                </div>
                <div className="registration__mobile-lo-card">
                  <LoanOfficerCard loInfo={loanOfficer} hideLabel />
                </div>
              </>
            )}
            {error == 500 && (
              <div className="registration__error">
                {isDesktop
                  ? Constant.RegistrationError
                  : Constant.RegistrationError.map((error) => {
                    return <p key={error}>{error}</p>;
                  })}
              </div>
            )}
            {phoneErrorMessage && <div className="registration__error">{phoneErrorMessage}</div>}
            <Form
              onSubmit={handleSubmit((data) => {
                onSubmit({
                  emailaddress: data.email as string,
                  password: data.password_input as string,
                  firstname: data.firstname as string,
                  lastname: data.lastname as string,
                  loanPurpose: data.loanpurpose as string,
                  phoneNumber: data.phone as string,
                  referralSource: "",
                  referredBy: "someone",
                  inquiryId: decryptedUserDetails?.id,
                  leadProvider: isBonzoSpouse ? LeadProvider.Bonzo : decryptedUserDetails?.lpn,
                  nhDealApplicationId: nhDealApplicationIdFromStore ? nhDealApplicationIdFromStore : decryptedUserDetails?.nhDealAppId,
                  userRole: decryptedUserDetails?.rt,
                  isTestLoan: decryptedUserDetails?.isTestLoan,
                  decryptPhoneNumber: decryptedUserDetails?.ph as string,
                  prospectId: decryptedUserDetails?.prospectId as string,
                }).catch(() => { setError(500) });
              })}
            >
              <div className="row--item">
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter Your Email Address",
                    label: "Email",
                    errorMessage: errors.email?.message as string,
                    isReadOnly: true,
                  }}
                />
              </div>
              <div className={!(!isPhoneNumberFetching && isPhoneRequired && isUserDetailsSuccess) && !decryptedUserDetails?.phmfapending ? "row--hidden" : "row--item"}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <PhoneNumberInput
                      onChange={(newValue: string) => {
                        onChange(newValue);
                      }}
                      value={value as string}
                      errorMessage={errors.phone?.message as string}
                      label="Phone Number"
                      placeholder={"(XXX) XXX-XXXX"}
                    />
                  )}
                />
              </div>
              <div className="row--item">
                <Controller
                  name="password_input"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <PasswordChecker
                      onChange={onChange}
                      checkErrors={ispasswordFieldModified}
                      createLabel="Create Password"
                      setHasPasswordError={setHasPwdError}
                      setBtnVisible={setIsBtnDisabled}
                      createPasswordPlaceholderName="Create password"
                    />
                  )}
                />
              </div>
              <Button title="Continue" className="registration__button" disabled={isBtnDisabled || !isPhoneValid} />
            </Form>
            {featureFlag?.splitConsentEnabled && <Consent text={Constant.PRIVACY_AND_TERMSOFUSE_CONSENT} />}
          </div>
          {isDesktop && (
            <div className="registration__information">
              {loanOfficer ? (
                <LoanOfficerCardV2 loInfo={loanOfficer} />
              ) : (
                <>
                  <p className="registration__text1">Better Path to Homeownership</p>
                  <p className="registration__text2">Seamless, Secure, and Built for You</p>
                  <p className="registration__text3">{formatContent(Constant.registrationInformation)}</p>
                  <img src={homeGraphic} alt="login-graphic" className="registration__graphic" />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {!featureFlag?.splitConsentEnabled && <RegistrationLegalConsent />}
      <div className="registration__footer-section"><Footer /></div>
    </>
  );
}
